import { useState, useCallback } from 'react';
import axiosService from 'utils/axios';
import { prepareForUpload } from 'utils/images/prepareForUpload';
import { UploadProgress, useFileUpload } from '../../hooks/use_file_upload';
import { FILE_TYPE_PICTURE } from '../../../config';
import { ErrorResponse } from 'types/general/types';
import {
  PictureToDisplay,
  createTemporaryPictureToDisplay,
  reorderPicturesToFront,
  removePicturesByIds,
} from '../utils/pictureUtils';

export interface UsePictureOperationsProps {
  vehicleId?: string;
  addingNewVehicle: boolean;
  userId?: string;
  selectedTabId?: string;
  onError?: (error: ErrorResponse | null) => void;
  setIsLoading?: (loading: boolean) => void;
  // Resolving false means the refresh failed and has already reported that through onError
  onSuccess?: () => void | Promise<boolean>;
}

const IDLE_PROGRESS: UploadProgress = {
  isUploading: false,
  currentFile: 0,
  totalFiles: 0,
  currentFileName: '',
  percentage: 0,
};

export const usePictureOperations = ({
  vehicleId,
  addingNewVehicle,
  userId,
  selectedTabId,
  onError,
  setIsLoading,
  onSuccess,
}: UsePictureOperationsProps) => {
  const { uploadFiles, uploadProgress } = useFileUpload();
  const [deleteProgress, setDeleteProgress] = useState<UploadProgress>(IDLE_PROGRESS);

  // State for temporary files and display pictures (used when adding a new vehicle)
  const [temporaryPictureFiles, setTemporaryPictureFiles] = useState<File[]>([]);
  const [temporaryDisplayPictures, setTemporaryDisplayPictures] = useState<PictureToDisplay[]>([]);
  // Map file names to their tab IDs for temporary pictures
  const [temporaryPictureTabMap, setTemporaryPictureTabMap] = useState<Map<string, string>>(new Map());

  const addPictures = useCallback(
    (files: File[]) => {
      if (addingNewVehicle) {
        // For new vehicles, add to temporary files and create display pictures
        const currentLength = temporaryPictureFiles.length;
        const newDisplayPictures = files.map((file, index) =>
          createTemporaryPictureToDisplay(file, currentLength + index, userId || '')
        );

        setTemporaryPictureFiles(prev => [...prev, ...files]);
        setTemporaryDisplayPictures(prev => [...prev, ...newDisplayPictures]);

        // Store tab assignment for each file
        if (selectedTabId) {
          setTemporaryPictureTabMap(prev => {
            const newMap = new Map(prev);
            files.forEach(file => {
              newMap.set(file.name, selectedTabId);
            });
            return newMap;
          });
        }
      } else if (vehicleId) {
        // For existing vehicles, upload directly with tab assignment
        setIsLoading?.(true);
        return uploadFiles(
          vehicleId,
          FILE_TYPE_PICTURE,
          files,
          () => {
            onError?.(null);
            onSuccess?.();
          },
          error => {
            onError?.(error);
          },
          true,
          selectedTabId
        ).finally(() => {
          setIsLoading?.(false);
        });
      }
      return Promise.resolve();
    },
    [
      addingNewVehicle,
      vehicleId,
      temporaryPictureFiles.length,
      userId,
      uploadFiles,
      onError,
      setIsLoading,
      onSuccess,
      selectedTabId,
    ]
  );

  const uploadTemporaryPictures = useCallback(
    async (vehicleIdToUpload: string) => {
      if (temporaryPictureFiles.length === 0) {
        return Promise.resolve();
      }

      setIsLoading?.(true);

      try {
        // Group files by their tab assignments
        const filesByTab = new Map<string, File[]>();

        temporaryPictureFiles.forEach(file => {
          const tabId = temporaryPictureTabMap.get(file.name) || 'initial';
          const filesInTab = filesByTab.get(tabId) || [];
          filesInTab.push(file);
          filesByTab.set(tabId, filesInTab);
        });

        // Upload each group of files to their respective tabs
        const uploadPromises: Promise<unknown>[] = [];

        filesByTab.forEach((files, tabId) => {
          const promise = uploadFiles(
            vehicleIdToUpload,
            FILE_TYPE_PICTURE,
            files,
            () => {
              // Success callback for individual group
            },
            error => {
              onError?.(error);
            },
            true,
            tabId
          );
          uploadPromises.push(promise);
        });

        await Promise.all(uploadPromises);
        onError?.(null);
        onSuccess?.();
      } catch (err) {
        onError?.(err as ErrorResponse);
      } finally {
        setIsLoading?.(false);
      }
    },
    [temporaryPictureFiles, temporaryPictureTabMap, uploadFiles, onError, setIsLoading, onSuccess]
  );

  const setAsCover = useCallback(
    async (pictureIndex: number, pictures: PictureToDisplay[]) => {
      if (addingNewVehicle) {
        // For new vehicles, reorder temporary files and display pictures
        setTemporaryPictureFiles(prev => {
          const reordered = [...prev];
          const [item] = reordered.splice(pictureIndex, 1);
          reordered.unshift(item);
          return reordered;
        });
        setTemporaryDisplayPictures(prev => reorderPicturesToFront(prev, pictureIndex));
      } else if (vehicleId) {
        // For existing vehicles, call API
        const pictureId = pictures[pictureIndex]?.id;
        if (!pictureId) return;

        setIsLoading?.(true);
        try {
          await axiosService.post(`/vehicles/vehicle/${vehicleId}/set_cover_picture/`, {
            // eslint-disable-next-line camelcase
            picture_id: pictureId,
          });
          onError?.(null);
          onSuccess?.();
        } catch (err) {
          onError?.(err as ErrorResponse);
        } finally {
          setIsLoading?.(false);
        }
      }
    },
    [addingNewVehicle, vehicleId, setIsLoading, onError, onSuccess]
  );

  const replacePicture = useCallback(
    async (pictureIndex: number, pictures: PictureToDisplay[], newPictureFile: File) => {
      if (addingNewVehicle) {
        // For new vehicles, replace in temporary files and display pictures
        const oldFileName = temporaryPictureFiles[pictureIndex]?.name;
        const oldTabId = oldFileName ? temporaryPictureTabMap.get(oldFileName) : undefined;

        setTemporaryPictureFiles(prev => {
          const updated = [...prev];
          updated[pictureIndex] = newPictureFile;
          return updated;
        });
        setTemporaryDisplayPictures(prev => {
          const updated = [...prev];
          updated[pictureIndex] = createTemporaryPictureToDisplay(newPictureFile, pictureIndex, userId || '');
          return updated;
        });

        // Update tab map: remove old file, add new file with same tab
        if (oldFileName && oldTabId) {
          setTemporaryPictureTabMap(prev => {
            const newMap = new Map(prev);
            newMap.delete(oldFileName);
            newMap.set(newPictureFile.name, oldTabId);
            return newMap;
          });
        }
      } else if (vehicleId) {
        // For existing vehicles, call API
        const pictureId = pictures[pictureIndex]?.id;
        if (!pictureId) return;

        setIsLoading?.(true);
        const formData = new FormData();
        formData.append('picture_id', pictureId);
        formData.append('new_picture_file', await prepareForUpload(newPictureFile));

        try {
          await axiosService.post(`/vehicles/vehicle/${vehicleId}/replace_picture/`, formData);
          onError?.(null);
          onSuccess?.();
        } catch (err) {
          onError?.(err as ErrorResponse);
        } finally {
          setIsLoading?.(false);
        }
      }
    },
    [
      addingNewVehicle,
      vehicleId,
      userId,
      temporaryPictureFiles,
      temporaryPictureTabMap,
      setIsLoading,
      onError,
      onSuccess,
    ]
  );

  /**
   * Deletes the pictures one request at a time and resolves with the ids that failed and can be retried.
   * A failure does not stop the batch: stopping left the rest of the selection undeleted with no
   * indication of which pictures remained (#104). A 403 is not retried: the backend's delete_picture
   * refuses a picture someone else uploaded unless the user can view all vehicles, so keeping it
   * selected would make every retry fail the same way.
   */
  const deletePictures = useCallback(
    async (indicesToDelete: number[], pictures: PictureToDisplay[]): Promise<string[]> => {
      if (addingNewVehicle) {
        // For new vehicles, remove from temporary files and display pictures
        // First, get the file names to remove from tab map
        const fileNamesToRemove = indicesToDelete.map(idx => temporaryPictureFiles[idx]?.name).filter(Boolean);

        setTemporaryPictureFiles(prev => prev.filter((_, idx) => !indicesToDelete.includes(idx)));

        // Get IDs to remove from display pictures
        const idsToRemove = new Set(indicesToDelete.map(idx => pictures[idx]?.id).filter(Boolean));
        setTemporaryDisplayPictures(prev => removePicturesByIds(prev, idsToRemove));

        // Remove from tab map
        if (fileNamesToRemove.length > 0) {
          setTemporaryPictureTabMap(prev => {
            const newMap = new Map(prev);
            fileNamesToRemove.forEach(fileName => {
              newMap.delete(fileName);
            });
            return newMap;
          });
        }
      } else if (vehicleId) {
        // For existing vehicles, call API
        const pictureIds = indicesToDelete.map(index => pictures[index]?.id).filter(Boolean);
        const failed: { id: string; error: string }[] = [];
        let forbiddenCount = 0;

        setIsLoading?.(true);
        for (let i = 0; i < pictureIds.length; i += 1) {
          setDeleteProgress({
            isUploading: true,
            currentFile: i + 1,
            totalFiles: pictureIds.length,
            currentFileName: '',
            percentage: Math.round((i / pictureIds.length) * 100),
            label: `Deleting ${i + 1} of ${pictureIds.length} pictures`,
          });
          try {
            await axiosService.post(`/vehicles/vehicle/${vehicleId}/delete_picture/`, {
              id: pictureIds[i],
            });
          } catch (err) {
            const { status, message, statusText } = err as ErrorResponse;
            if (String(status) === '403') {
              forbiddenCount += 1;
            } else if (String(status) !== '404') {
              // 404: already deleted, e.g. from another tab; reselecting it would leave a selection that can't be cleared
              failed.push({ id: pictureIds[i], error: message || statusText || 'Delete failed' });
            }
          }
        }
        setDeleteProgress(IDLE_PROGRESS);
        setIsLoading?.(false);

        const deletedCount = pictureIds.length - failed.length - forbiddenCount;
        // Awaited so the summary below is reported after the refresh, and can't be replaced by its error
        const refreshed = deletedCount > 0 ? (await onSuccess?.()) !== false : true;

        if (failed.length === 0 && forbiddenCount === 0) {
          // A failed refresh has already reported itself; clearing here would hide that
          if (refreshed) {
            onError?.(null);
          }
        } else {
          const parts = [`Deleted ${deletedCount} of ${pictureIds.length} pictures.`];
          if (forbiddenCount > 0) {
            parts.push(
              `${forbiddenCount} ${
                forbiddenCount > 1 ? 'were' : 'was'
              } not deleted because you can delete only the pictures you uploaded.`
            );
          }
          if (failed.length > 0) {
            const reasons = Array.from(new Set(failed.map(f => f.error))).join('\n');
            parts.push(`${failed.length} failed and ${failed.length > 1 ? 'are' : 'is'} still selected:\n${reasons}`);
          }
          const reloadNote = refreshed
            ? ''
            : '\nThe pictures could not be reloaded. Reload the page to see which remain.';
          onError?.({
            status: deletedCount > 0 ? 'warning' : 'error',
            statusText: deletedCount > 0 ? 'Partial Delete' : 'Delete Failed',
            message: `${parts.join(' ')}${reloadNote}`,
          });
        }
        return failed.map(f => f.id);
      }
      return [];
    },
    [addingNewVehicle, vehicleId, temporaryPictureFiles, setIsLoading, onError, onSuccess]
  );

  return {
    addPictures,
    setAsCover,
    replacePicture,
    deletePictures,
    uploadTemporaryPictures,
    uploadProgress,
    deleteProgress,
    temporaryPictureFiles,
    temporaryDisplayPictures,
  };
};
